import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { IUser, IUserDetails } from '../interface/user';
import { useLanguage } from './LanguageContext';
import { getHostLocale, setRegionLocale } from '../constants/locale';
import { normalizeAppLanguage } from '../constants/constants';

interface UserContextType {
  user: IUser | null;
  updateUser: (userData: IUser) => void;
  userDetails: IUserDetails | null;
  updateUserDetails: (userData: IUserDetails | undefined) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<IUser | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [userDetails, setUserDetails] = useState<IUserDetails | null>(null);
  const { language, changeLanguage } = useLanguage();

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  const updateUser = useCallback((userData: IUser) => {
    setUser(userData);
  }, []);
  const updateUserDetails = useCallback(
    (userData: IUserDetails | undefined) => {
      if (!userData) return;
      setUserDetails(userData);
      setRegionLocale(userData.uiRegionalFormat);

      const resolvedLanguage = normalizeAppLanguage(userData.uiLanguage || getHostLocale());
      if (resolvedLanguage !== language) {
        changeLanguage(resolvedLanguage);
      }
    },
    [language, changeLanguage],
  );

  const userContextValue = useMemo(
    () => ({ user, updateUser, updateUserDetails, userDetails }),
    [user, updateUser, updateUserDetails, userDetails],
  );
  return <UserContext.Provider value={userContextValue}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
